import { PipelineFilters } from '@/modules/pipelines/contexts/pipeline-notification-context';
import React, { forwardRef, useMemo, useState } from 'react';
import { EntityNode } from '@/modules/pipelines/model/pipeline-alerts/entity-node';
import { EntityType } from '@/modules/pipelines/model/pipeline-alerts/entity-type';
import {
  Dropdown,
  DropdownItemText,
  DropdownMenu,
  DropdownToggle,
  FormCheck,
  FormControl,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faScrewdriverWrench, faWrench } from '@fortawesome/free-solid-svg-icons';
import { PipelineEventType } from '@/modules/pipelines/model/pipeline-alerts/pipeline-event-type';

interface CustomDropdownProps extends React.HTMLAttributes<HTMLElement> {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  isEntity: boolean;
}

const CustomDropdown = forwardRef<HTMLInputElement, CustomDropdownProps>(
  ({ onClick, searchQuery, setSearchQuery, isEntity }, ref) => {
    return (
      <div className="position-relative">
        <FormControl
          id={`${isEntity ? 'p' : 'e'}-control`}
          ref={ref}
          size="sm"
          placeholder="Search"
          value={searchQuery}
          onClick={e => {
            e.preventDefault();
            if (onClick) onClick(e);
          }}
          onFocus={e => e.target.select()}
          onChange={e => {
            setSearchQuery(e.target.value);
          }}
        />
        <FontAwesomeIcon
          size="lg"
          className="pe-none position-absolute top-50 end-0 translate-middle-y p-0"
          style={{ marginInlineEnd: '0.75rem' }}
          icon={faAngleDown}
        />
      </div>
    );
  },
);

export default function PipelineEntityFilterControl({
  filters,
  setFilters,
  items,
  formRef,
}: {
  filters: PipelineFilters;
  setFilters: React.Dispatch<React.SetStateAction<PipelineFilters>>;
  items: EntityNode[] | PipelineEventType[];
  formRef: React.RefObject<HTMLFormElement>;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(80); // could be useref

  function hasKey<T extends object[], K extends string>(
    items: T,
    key: K,
  ): items is Extract<T[number], Record<K, any>>[] & T {
    return items.length > 0 && key in items[0];
  }

  const isEntity = hasKey(items, 'relations');
  const isEvent = hasKey(items, 'parentIds');

  const itemGraph = useMemo(() => {
    const itemMap = new Map<string, EntityNode | PipelineEventType>();
    const parentToChildren = new Map<string, Set<string>>();
    const allChildren = new Set();
    const unrelatedPipes = new Set<EntityNode>();

    if (isEntity) {
      items.forEach(item => {
        item.relations.forEach(rel => {
          const target = items.find(i => i._id === rel.targetId);
          if (target?.type !== EntityType.PIPELINE) return;
          if (!itemMap.has(item._id)) itemMap.set(item._id, item);

          allChildren.add(rel.targetId);
          if (!parentToChildren.has(rel.sourceId)) parentToChildren.set(rel.sourceId, new Set());
          parentToChildren.get(rel.sourceId)?.add(rel.targetId);
        });
        if (item.type === EntityType.PIPELINE && item.relations.length === 0)
          unrelatedPipes.add(item);
      });
    } else if (isEvent) {
      items.forEach(item => {
        itemMap.set(item._id, item);
        if (item.parentIds.length > 0) allChildren.add(item._id);

        item.parentIds.forEach(id => {
          if (!parentToChildren.has(id)) parentToChildren.set(id, new Set());
          parentToChildren.get(id)?.add(item._id);
        });
      });
    }

    if (unrelatedPipes.size > 0) {
      itemMap.set('REGION:Unassigned', {
        _id: 'REGION:Unassigned',
        externalId: 'REGION:Unassigned',
        name: 'Unassigned',
        type: EntityType.REGION,
        relations: [],
        paths: [],
        createdAt: new Date(),
        modifiedAt: new Date(),
      });
      parentToChildren.set('REGION:Unassigned', new Set());

      for (const pipe of unrelatedPipes) {
        parentToChildren.get('REGION:Unassigned')?.add(pipe._id);
        itemMap.set(pipe._id, pipe);
        allChildren.add(pipe._id);
      }
    }

    const rootIds = Array.from(itemMap.keys()).filter(id => !allChildren.has(id));
    return { itemMap, parentToChildren, rootIds };
    // eslint-disable-next-line
  }, [items]);

  const visItems = (() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return null;
    return Array.from(itemGraph.itemMap.values()).filter(item =>
      item.name.toLowerCase().includes(query),
    );
  })();

  const checkSet = (() => {
    const nextSet = new Set<string>();
    if (
      (isEntity && !filters.operator) ||
      (isEvent && !filters.eventTypeClassification) ||
      items.length === 0
    )
      return nextSet;

    const activeFilters = isEntity
      ? new Set(filters.operator)
      : new Set(filters.eventTypeClassification);

    if (isEntity) {
      items.forEach(item => {
        if (item.pipeline && activeFilters.has(item.pipeline)) nextSet.add(item._id);
      });
    } else if (isEvent) {
      items.forEach(item => {
        if (item.scheme === 'type' && activeFilters.has(item._id)) nextSet.add(item._id);
      });
    }

    itemGraph.rootIds.forEach(rootId => {
      const children = itemGraph.parentToChildren.get(rootId) || new Set();
      if (children.size === 0) return;
      const allChildrenChecked = Array.from(children).every(cId => nextSet.has(cId));
      if (allChildrenChecked) nextSet.add(rootId);
    });

    return nextSet;
  })();

  const handleStateChange = (id: string, isChecked: boolean) => {
    const { itemMap, parentToChildren } = itemGraph;
    const item = itemMap.get(id);
    if (!item) return;

    const updatedCheckSet = new Set(checkSet);

    const isChild =
      isEntity && /^pipeline/i.test(item._id)
        ? true
        : isEvent && item._id.startsWith('type')
          ? true
          : false;

    if (isChild) isChecked ? updatedCheckSet.add(id) : updatedCheckSet.delete(id);
    else {
      const children = parentToChildren.get(id) || new Set();
      children.forEach(childId =>
        isChecked ? updatedCheckSet.add(childId) : updatedCheckSet.delete(childId),
      );
    }

    const nextActiveFilters = new Set<string>();
    if (isEntity) {
      items.forEach(item => {
        if (updatedCheckSet.has(item._id) && item.pipeline) nextActiveFilters.add(item.pipeline);
      });
    } else if (isEvent) {
      items.forEach(item => {
        const isChildItem = item._id.startsWith('type');
        if (updatedCheckSet.has(item._id) && isChildItem) nextActiveFilters.add(item._id);
      });
    }

    setFilters(prev => ({
      ...prev,
      ...(isEntity
        ? { operator: Array.from(nextActiveFilters) }
        : { eventTypeClassification: Array.from(nextActiveFilters) }),
    }));
  };

  const allChecked = (() => {
    const { itemMap } = itemGraph;
    console.log('all items: ', itemMap);
    console.log('all items checkset: ', checkSet);
    return itemMap.size > 0 && checkSet.size === itemMap.size;
  })();

  const handleAllStateChange = () => {
    if (allChecked)
      setFilters(prev => ({
        ...prev,
        ...(isEntity ? { operator: [] } : { eventTypeClassification: [] }),
      }));
    else
      setFilters(prev => ({
        ...prev,
        ...(isEntity
          ? {
              operator: items.flatMap(item =>
                typeof item.pipeline === 'string' ? [item.pipeline] : [],
              ),
            }
          : {
              eventTypeClassification: items
                .map(item => item._id)
                .filter(id => id.startsWith('type')),
            }),
      }));
  };

  const calculateDropdownHeight = () => {
    const viewportEl = formRef.current?.closest(
      '#pipeline-scroller-viewport',
    ) as HTMLElement | null;
    setDropdownMaxHeight(
      Math.max(80, viewportEl ? viewportEl.offsetHeight - 240 : window.innerHeight),
    );
  };

  return (
    <div>
      <span className="text-text-dark fw-semibold" style={{ fontSize: '0.9rem' }}>
        {isEntity ? 'Pipeline' : 'Event'}
      </span>
      <Dropdown onToggle={calculateDropdownHeight}>
        <DropdownToggle
          as={CustomDropdown}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          isEntity={isEntity}
        />

        <DropdownMenu className="border-white w-100">
          <div>
            <DropdownItemText>
              <FormCheck
                label={<strong>ALL</strong>}
                checked={allChecked}
                onChange={() => handleAllStateChange()}
              />
            </DropdownItemText>
          </div>
          <div style={{ maxHeight: dropdownMaxHeight, overflowY: 'auto' }}>
            {visItems !== null
              ? visItems.map(item => {
                  const isChecked = checkSet.has(item._id);
                  return (
                    <DropdownItemText>
                      <FormCheck
                        onChange={() => handleStateChange(item._id, !isChecked)}
                        checked={isChecked}
                        label={
                          <span>
                            {isEntity &&
                              (item as any)?.devState &&
                              (item as any)?.devState !== 'release' && (
                                <FontAwesomeIcon
                                  icon={
                                    (item as any)?.devState === 'alpha'
                                      ? faScrewdriverWrench
                                      : faWrench
                                  }
                                />
                              )}{' '}
                            {item.name}
                          </span>
                        }
                      />
                    </DropdownItemText>
                  );
                })
              : itemGraph.rootIds.map(id => (
                  <RecursiveDropdown
                    key={id}
                    nodeId={id}
                    itemMap={itemGraph.itemMap}
                    adjacencyList={itemGraph.parentToChildren}
                    checkSet={checkSet}
                    exclusive={[EntityType.REGION, EntityType.PIPELINE]}
                    onCheckChange={handleStateChange}
                  />
                ))}
          </div>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

function RecursiveDropdown({
  nodeId,
  itemMap,
  adjacencyList,
  checkSet,
  onCheckChange,
  exclusive,
}: {
  nodeId: string;
  itemMap: Map<string, any>;
  adjacencyList: Map<string, Set<string>>;
  checkSet: Set<string>;
  onCheckChange: (id: string, newState: boolean) => void;
  exclusive?: EntityType[];
}) {
  const item = itemMap.get(nodeId);
  if (!item) return null;

  if (exclusive && 'type' in item && !exclusive.includes(item.type)) return null;

  const childIds = Array.from(adjacencyList.get(nodeId) || []);
  const isChecked = checkSet.has(nodeId) || false;

  const isPipeline = item.type === EntityType.PIPELINE || /^pipeline/i.test(item._id);

  return (
    <DropdownItemText>
      <FormCheck
        onChange={() => onCheckChange(item._id, !isChecked)}
        checked={isChecked}
        label={
          <span>
            {(item as any)?.devState && (item as any)?.devState !== 'release' && (
              <FontAwesomeIcon
                icon={(item as any)?.devState === 'alpha' ? faScrewdriverWrench : faWrench}
              />
            )}{' '}
            {item.name || nodeId}
          </span>
        }
      />

      {!isPipeline && childIds.length > 0 && (
        <div className="ms-3">
          {childIds.map(id => (
            <RecursiveDropdown
              key={id}
              nodeId={id}
              itemMap={itemMap}
              adjacencyList={adjacencyList}
              checkSet={checkSet}
              exclusive={exclusive}
              onCheckChange={onCheckChange}
            />
          ))}
        </div>
      )}
    </DropdownItemText>
  );
}
